// ./src/components/features/Connectors/ConnectorConfigEditor.tsx
import React from "react";
import { Text } from "@mantine/core";
import { CONNECTOR_TYPES } from "@/config/connectorCategories";
import { ConnectorHttpConfigEditor } from "./ConnectorHttpConfigEditor";
import { AzureQueueConnectorConfigEditor } from "./AzureQueueConnectorConfigEditor";
import { AzureBlobConnectorConfigEditor } from "./AzureBlobConnectorConfigEditor";
import { AzureTableConnectorConfigEditor } from "./AzureTableConnectorConfigEditor";
import { AzureServiceBusConnectorConfigEditor } from "./AzureServiceBusConnectorConfigEditor";
import { AzureEventGridConnectorConfigEditor } from "./AzureEventGridConnectorConfigEditor";

import { AwsS3ConnectorConfigEditor } from "./AwsS3ConnectorConfigEditor";
import { AwsSqsConnectorConfigEditor } from "./AwsSqsConnectorConfigEditor";
import { AwsSnsConnectorConfigEditor } from "./AwsSnsConnectorConfigEditor";
import { AwsEventBridgeConnectorConfigEditor } from "./AwsEventBridgeConnectorConfigEditor";
import { AwsDynamoDbConnectorConfigEditor } from "./AwsDynamoDbConnectorConfigEditor";

import { GcpStorageConnectorConfigEditor } from "./GcpStorageConnectorConfigEditor";
import { GcpPubSubConnectorConfigEditor } from "./GcpPubSubConnectorConfigEditor";
import { GcpFirestoreConnectorConfigEditor } from "./GcpFirestoreConnectorConfigEditor";

import { SlackConnectorConfigEditor } from "./SlackConnectorConfigEditor";
import { TelegramConnectorConfigEditor } from "./TelegramConnectorConfigEditor";
import { DiscordConnectorConfigEditor } from "./DiscordConnectorConfigEditor";
import { HubSpotConnectorConfigEditor } from "./HubSpotConnectorConfigEditor";
import { GoogleSheetsConnectorConfigEditor } from "./GoogleSheetsConnectorConfigEditor";
import { NotionConnectorConfigEditor } from "./NotionConnectorConfigEditor";
import { AirtableConnectorConfigEditor } from "./AirtableConnectorConfigEditor";
import { TrelloConnectorConfigEditor } from "./TrelloConnectorConfigEditor";
import { GitHubConnectorConfigEditor } from "./GitHubConnectorConfigEditor";
import { MailchimpConnectorConfigEditor } from "./MailchimpConnectorConfigEditor";
import { ConvertKitConnectorConfigEditor } from "./ConvertKitConnectorConfigEditor";
import {
  FtpConnectorConfigEditor,
  FtpsConnectorConfigEditor,
} from "./FtpConnectorConfigEditor";
import { SendGridConnectorConfigEditor } from "./SendGridConnectorConfigEditor";
import { SmtpConnectorConfigEditor } from "./SmtpConnectorConfigEditor";
import { AwsSesConnectorConfigEditor } from "./AwsSesConnectorConfigEditor";

/**
 * Common interface for all connector config editors
 */
export interface ConnectorConfigEditorComponentProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Map of connector types to their config editor components
 */
const CONNECTOR_CONFIG_EDITORS: Record<
  string,
  React.FC<ConnectorConfigEditorComponentProps>
> = {
  // HTTP
  [CONNECTOR_TYPES.HTTP]: ConnectorHttpConfigEditor,

  // FTP/FTPS
  [CONNECTOR_TYPES.FTP]: FtpConnectorConfigEditor,
  [CONNECTOR_TYPES.FTPS]: FtpsConnectorConfigEditor,

  // Azure
  [CONNECTOR_TYPES.AZURE_STORAGE_ACCOUNT_QUEUE]:
    AzureQueueConnectorConfigEditor,
  [CONNECTOR_TYPES.AZURE_STORAGE_ACCOUNT_BLOB]: AzureBlobConnectorConfigEditor,
  [CONNECTOR_TYPES.AZURE_STORAGE_ACCOUNT_TABLE]:
    AzureTableConnectorConfigEditor,
  [CONNECTOR_TYPES.AZURE_SERVICE_BUS]: AzureServiceBusConnectorConfigEditor,
  [CONNECTOR_TYPES.AZURE_EVENT_GRID]: AzureEventGridConnectorConfigEditor,

  // AWS
  [CONNECTOR_TYPES.AWS_S3]: AwsS3ConnectorConfigEditor,
  [CONNECTOR_TYPES.AWS_SQS]: AwsSqsConnectorConfigEditor,
  [CONNECTOR_TYPES.AWS_SNS]: AwsSnsConnectorConfigEditor,
  [CONNECTOR_TYPES.AWS_EVENTBRIDGE]: AwsEventBridgeConnectorConfigEditor,
  [CONNECTOR_TYPES.AWS_DYNAMODB]: AwsDynamoDbConnectorConfigEditor,

  // GCP
  [CONNECTOR_TYPES.GCP_STORAGE]: GcpStorageConnectorConfigEditor,
  [CONNECTOR_TYPES.GCP_PUBSUB]: GcpPubSubConnectorConfigEditor,
  [CONNECTOR_TYPES.GCP_FIRESTORE]: GcpFirestoreConnectorConfigEditor,

  // Messaging
  [CONNECTOR_TYPES.SLACK]: SlackConnectorConfigEditor,
  [CONNECTOR_TYPES.TELEGRAM]: TelegramConnectorConfigEditor,
  [CONNECTOR_TYPES.DISCORD]: DiscordConnectorConfigEditor,

  // CRM
  [CONNECTOR_TYPES.HUBSPOT]: HubSpotConnectorConfigEditor,

  // PRODUCTIVITY
  [CONNECTOR_TYPES.GOOGLE_SHEETS]: GoogleSheetsConnectorConfigEditor,
  [CONNECTOR_TYPES.NOTION]: NotionConnectorConfigEditor,
  [CONNECTOR_TYPES.AIRTABLE]: AirtableConnectorConfigEditor,
  [CONNECTOR_TYPES.TRELLO]: TrelloConnectorConfigEditor,
  [CONNECTOR_TYPES.GITHUB]: GitHubConnectorConfigEditor,
  [CONNECTOR_TYPES.MAILCHIMP]: MailchimpConnectorConfigEditor,
  [CONNECTOR_TYPES.CONVERTKIT]: ConvertKitConnectorConfigEditor,

  // Email
  [CONNECTOR_TYPES.SENDGRID]: SendGridConnectorConfigEditor,
  [CONNECTOR_TYPES.SMTP]: SmtpConnectorConfigEditor,
  [CONNECTOR_TYPES.AWS_SES]: AwsSesConnectorConfigEditor,
};

export interface ConnectorConfigEditorProps {
  /** Connector type to determine which editor to render */
  connectorType: string | null | undefined;

  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

export const ConnectorConfigEditor: React.FC<ConnectorConfigEditorProps> = ({
  connectorType,
  value,
  onChange,
  disabled = false,
}) => {
  // No type selected
  if (!connectorType) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="md">
        Select a connector type to configure its settings.
      </Text>
    );
  }

  // Look up the editor component
  const EditorComponent = CONNECTOR_CONFIG_EDITORS[connectorType];

  // No editor available for this type
  if (!EditorComponent) {
    return null;
  }

  return (
    <EditorComponent value={value} onChange={onChange} disabled={disabled} />
  );
};
